import classNames from 'classnames';
import type { HTMLAttributes, MouseEventHandler, ReactNode } from 'react';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { GlowCard } from '@sorare/core/src/components/card/GlowCard';
import { StrokedCard } from '@sorare/core/src/components/card/StrokedCard';
import type { Color, RGBColor } from '@sorare/core/src/style/types';
import { css } from '@sorare/css';

const root = css`
  position: relative;
  isolation: isolate;
`;
const card = css`
  position: relative;
  isolation: isolate;
  aspect-ratio: var(--card-aspect-ratio);
  transition: filter 0.3s ease-in-out;
  &:not(.disabled) {
    &:hover {
      filter: brightness(120%);
    }
  }
`;

const cornerBadgeClass = css`
  position: absolute;
  right: 0;
  top: var(--unit);

  &.selected {
    right: -2px;
  }
`;

type Props = {
  children: ReactNode;
  footer: ReactNode;
  cornerBadge?: ReactNode;
  selected?: boolean;
  onClickCard?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  ariaLabel?: string;
  className?: string;
  maskImageSrc: string | null;
  disabled?: boolean;
  glowColor?: RGBColor;
} & Pick<HTMLAttributes<HTMLDivElement>, 'onPointerEnter' | 'onPointerLeave'>;

export const BasicBenchCard = ({
  children,
  footer,
  cornerBadge,
  selected,
  disabled,
  onClickCard,
  ariaLabel,
  className,
  maskImageSrc,
  glowColor,
  onPointerEnter,
  onPointerLeave,
}: Props) => {
  const active = !!(selected || disabled);

  const { stroke, glow }: { stroke: Color; glow: Color } = (() => {
    if (disabled) {
      return {
        stroke: 'var(--c-nd-600)',
        glow: 'var(--c-nd-600)',
      };
    }
    return {
      stroke: 'var(--c-blue-400)',
      glow: 'rgba(var(--c-rgb-blue-400), 0.8)',
    };
  })();

  return (
    <Vertical gap={1} className={classNames(root, className)}>
      <ConditionalWrapper
        Wrapper={ButtonBase}
        wrap={!!onClickCard}
        props={{ onClick: onClickCard, disableDebounce: false }}
      >
        <ConditionalWrapper
          Wrapper={GlowCard}
          wrap={!!glowColor}
          props={{ active: !!glowColor, color: glowColor! }}
        >
          <StrokedCard
            active={active}
            color={stroke}
            withGlow={{
              src: maskImageSrc || '',
              color: glow,
            }}
          >
            <div
              className={classNames(card, { disabled })}
              aria-label={ariaLabel}
              onPointerEnter={onPointerEnter}
              onPointerLeave={onPointerLeave}
            >
              {children}
            </div>
          </StrokedCard>
        </ConditionalWrapper>
      </ConditionalWrapper>
      {footer}
      {!!cornerBadge && (
        <div className={classNames(cornerBadgeClass, { selected })}>
          {cornerBadge}
        </div>
      )}
    </Vertical>
  );
};
